import { useRef, useState } from 'react'
import { tokenSpeicher } from '../api/client'

interface Tagesart {
  nummer: string
  bezeichnung: string
  anzahlFahrten: number
  anzahlBetriebstage: number
}

interface Uebersicht {
  quelle: string | null
  tabellen: string[]
  fehlendeTabellen: string[]
  tagesarten: Tagesart[]
}

interface Fahrtangabe {
  linie: string
  abfahrt: string
  ankunft: string
  dauerMinuten: number
  von: string
  nach: string
}

interface Umlaufangabe {
  nummer: number
  beginn: string
  ende: string
  einsatzdauerMinuten: number
  fahrzeitMinuten: number
  leerfahrtMinuten: number
  wartezeitMinuten: number
  auslastung: number
  anzahlFahrten: number
  fahrten: Fahrtangabe[]
}

interface Ergebnis {
  tagesart: string
  anzahlFahrten: number
  fahrtenOhneFahrzeit: number
  anzahlUmlaeufe: number
  fahrzeitMinuten: number
  leerfahrtMinuten: number
  wartezeitMinuten: number
  einsatzdauerMinuten: number
  auslastung: number
  kantenGeprueft: number
  rechenzeitMillis: number
  umlaeufe: Umlaufangabe[]
}

function stunden(minuten: number): string {
  return `${Math.floor(minuten / 60)}:${String(minuten % 60).padStart(2, '0')} h`
}

/** Multipart-Aufrufe mit Token — der API-Client kennt keine Query-Parameter bei Uploads. */
async function sende<T>(pfad: string, datei: File): Promise<T> {
  const formular = new FormData()
  formular.append('datei', datei)
  const antwort = await fetch(pfad, {
    method: 'POST',
    headers: { Authorization: `Bearer ${tokenSpeicher.access()}` },
    body: formular,
  })
  if (!antwort.ok) {
    const koerper = await antwort.json().catch(() => ({}))
    throw new Error(koerper.message || `Fehler ${antwort.status}`)
  }
  return antwort.json()
}

export function UmlaufbildungSeite() {
  const [datei, setDatei] = useState<File | null>(null)
  const [uebersicht, setUebersicht] = useState<Uebersicht | null>(null)
  const [tagesart, setTagesart] = useState('')
  const [wendezeit, setWendezeit] = useState(5)
  const [geschwindigkeit, setGeschwindigkeit] = useState(25)
  const [ergebnis, setErgebnis] = useState<Ergebnis | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [offen, setOffen] = useState<number | null>(null)
  const feld = useRef<HTMLInputElement | null>(null)

  async function lieferungLesen(gewaehlt: File | undefined) {
    if (!gewaehlt) return
    setDatei(gewaehlt)
    setUebersicht(null)
    setErgebnis(null)
    setFehler(null)
    setLaeuft(true)
    try {
      const u = await sende<Uebersicht>('/api/vdv/uebersicht', gewaehlt)
      setUebersicht(u)
      if (u.tagesarten.length > 0) setTagesart(u.tagesarten[0].nummer)
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Lieferung konnte nicht gelesen werden')
    } finally {
      setLaeuft(false)
    }
  }

  async function rechnen() {
    if (!datei || !tagesart) return
    setLaeuft(true)
    setFehler(null)
    setErgebnis(null)
    try {
      setErgebnis(
        await sende<Ergebnis>(
          `/api/vdv/umlaufbildung?tagesart=${encodeURIComponent(tagesart)}` +
            `&wendezeitMinuten=${wendezeit}&leerfahrtKmProStunde=${geschwindigkeit}`,
          datei,
        ),
      )
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Berechnung fehlgeschlagen')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Umlaufbildung</h1>
          <p>
            Wie viele Fahrzeuge braucht dieser Fahrplan mindestens? Berechnet aus einer
            VDV-452-Lieferung.
          </p>
        </div>
      </div>

      {fehler && <div className="meldung fehler">{fehler}</div>}

      <div className="karte abschnitt">
        <input
          ref={feld}
          type="file"
          accept=".zip"
          style={{ display: 'none' }}
          onChange={(e) => void lieferungLesen(e.target.files?.[0])}
        />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="knopf-akzent" disabled={laeuft} onClick={() => feld.current?.click()}>
            VDV-Lieferung auswählen
          </button>
          {datei && (
            <span style={{ color: 'var(--grau-700)', fontSize: '0.88rem' }}>
              {datei.name} · {Math.round(datei.size / 1024)} KB
            </span>
          )}
        </div>

        {uebersicht && (
          <>
            <p style={{ color: 'var(--grau-500)', fontSize: '0.85rem', marginTop: 14 }}>
              Quelle: {uebersicht.quelle ?? 'unbekannt'} · {uebersicht.tabellen.length} Tabellen
              gelesen
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 14,
                marginTop: 10,
              }}
            >
              <div className="feld">
                <label htmlFor="tagesart">Tagesart</label>
                <select
                  id="tagesart"
                  value={tagesart}
                  onChange={(e) => setTagesart(e.target.value)}
                >
                  {uebersicht.tagesarten.map((t) => (
                    <option key={t.nummer} value={t.nummer}>
                      {t.nummer} — {t.anzahlFahrten} Fahrten an {t.anzahlBetriebstage} Tagen
                    </option>
                  ))}
                </select>
              </div>
              <div className="feld">
                <label htmlFor="wendezeit">Mindestwendezeit (Minuten)</label>
                <input
                  id="wendezeit"
                  type="number"
                  min={0}
                  max={60}
                  value={wendezeit}
                  onChange={(e) => setWendezeit(Number(e.target.value))}
                />
              </div>
              <div className="feld">
                <label htmlFor="tempo">Leerfahrt-Geschwindigkeit (km/h)</label>
                <input
                  id="tempo"
                  type="number"
                  min={10}
                  max={80}
                  value={geschwindigkeit}
                  onChange={(e) => setGeschwindigkeit(Number(e.target.value))}
                />
              </div>
            </div>

            <button className="knopf-primaer" disabled={laeuft || !tagesart} onClick={() => void rechnen()}>
              {laeuft ? 'Wird berechnet …' : 'Umläufe bilden'}
            </button>
          </>
        )}
      </div>

      {ergebnis && (
        <>
          <div className="karten-raster">
            <div className="kennzahl navy">
              <div className="wert">{ergebnis.anzahlUmlaeufe}</div>
              <div className="beschriftung">Fahrzeuge mindestens nötig</div>
            </div>
            <div className="kennzahl">
              <div className="wert">{ergebnis.anzahlFahrten}</div>
              <div className="beschriftung">Fahrten an diesem Tag</div>
            </div>
            <div className="kennzahl">
              <div className="wert">{Math.round(ergebnis.auslastung * 100)}%</div>
              <div className="beschriftung">Anteil Fahrgastfahrten an der Einsatzzeit</div>
            </div>
            <div className="kennzahl orange">
              <div className="wert">{stunden(ergebnis.leerfahrtMinuten)}</div>
              <div className="beschriftung">geschätzte Leerfahrten</div>
            </div>
          </div>

          <div className="meldung hinweis">
            <strong>Wie belastbar ist das?</strong> Die Fahrzeuganzahl ist{' '}
            <strong>beweisbar minimal</strong> — sie ergibt sich aus einem maximalen Matching,
            nicht aus einer Schätzung. Die Leerfahrzeiten dagegen sind{' '}
            <strong>geschätzt</strong>: Die Lieferung enthält keine Übergangsfahrzeiten, deshalb
            Luftlinie mit 30 Prozent Umwegzuschlag. Nicht berücksichtigt sind Ein- und
            Ausrückfahrten zum Betriebshof sowie Beschränkungen auf bestimmte Fahrzeugtypen.
            {ergebnis.fahrtenOhneFahrzeit > 0 && (
              <>
                {' '}
                <strong>{ergebnis.fahrtenOhneFahrzeit} Fahrten</strong> ließen sich mangels
                Streckenfahrzeiten nicht berechnen und fehlen in der Rechnung.
              </>
            )}
          </div>

          <div className="karte abschnitt">
            <h2>Zeitbilanz aller Fahrzeuge</h2>
            <div className="tabelle-huelle" style={{ boxShadow: 'none' }}>
              <table>
                <tbody>
                  <tr>
                    <td>Fahrgastfahrzeit</td>
                    <td className="zahl">
                      <strong>{stunden(ergebnis.fahrzeitMinuten)}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td>Leerfahrten zwischen den Fahrten</td>
                    <td className="zahl">{stunden(ergebnis.leerfahrtMinuten)}</td>
                  </tr>
                  <tr>
                    <td>Standzeiten an den Endpunkten</td>
                    <td className="zahl">{stunden(ergebnis.wartezeitMinuten)}</td>
                  </tr>
                  <tr>
                    <td>Gesamte Fahrzeugeinsatzzeit</td>
                    <td className="zahl">
                      <strong>{stunden(ergebnis.einsatzdauerMinuten)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p style={{ color: 'var(--grau-500)', fontSize: '0.82rem', marginTop: 10 }}>
              {ergebnis.kantenGeprueft.toLocaleString('de-DE')} mögliche Verkettungen geprüft,
              Rechenzeit {ergebnis.rechenzeitMillis} ms.
            </p>
          </div>

          <div className="tabelle-huelle">
            <table>
              <thead>
                <tr>
                  <th>Umlauf</th>
                  <th>Zeitraum</th>
                  <th>Einsatzdauer</th>
                  <th>Fahrzeit</th>
                  <th>Leerfahrt</th>
                  <th>Standzeit</th>
                  <th>Auslastung</th>
                  <th>Fahrten</th>
                </tr>
              </thead>
              <tbody>
                {ergebnis.umlaeufe.map((u) => (
                  <tr
                    key={u.nummer}
                    className="klickbar"
                    onClick={() => setOffen(offen === u.nummer ? null : u.nummer)}
                  >
                    <td>
                      <strong>{u.nummer}</strong>
                    </td>
                    <td className="zahl">
                      {u.beginn} – {u.ende}
                    </td>
                    <td className="zahl">{stunden(u.einsatzdauerMinuten)}</td>
                    <td className="zahl">{stunden(u.fahrzeitMinuten)}</td>
                    <td className="zahl">{stunden(u.leerfahrtMinuten)}</td>
                    <td className="zahl">{stunden(u.wartezeitMinuten)}</td>
                    <td>
                      <span
                        className={`etikett ${
                          u.auslastung > 0.6 ? 'gruen' : u.auslastung > 0.4 ? 'orange' : 'rot'
                        }`}
                      >
                        {Math.round(u.auslastung * 100)}%
                      </span>
                    </td>
                    <td className="zahl">{u.anzahlFahrten}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {offen !== null && (
            <div className="karte abschnitt" style={{ marginTop: 14 }}>
              <h2>Umlauf {offen} im Einzelnen</h2>
              <div className="tabelle-huelle" style={{ boxShadow: 'none' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Ab</th>
                      <th>An</th>
                      <th>Dauer</th>
                      <th>Linie</th>
                      <th>Von</th>
                      <th>Nach</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ergebnis.umlaeufe
                      .find((u) => u.nummer === offen)
                      ?.fahrten.map((f, i) => (
                        <tr key={i}>
                          <td className="zahl">{f.abfahrt}</td>
                          <td className="zahl">{f.ankunft}</td>
                          <td className="zahl">{f.dauerMinuten}′</td>
                          <td>
                            <span className="etikett navy">{f.linie}</span>
                          </td>
                          <td>{f.von}</td>
                          <td>{f.nach}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {!uebersicht && !laeuft && (
        <div className="karte">
          <h2>Was hier berechnet wird</h2>
          <p style={{ color: 'var(--grau-700)', lineHeight: 1.7 }}>
            Aus den Fahrten eines Betriebstags wird ermittelt, wie viele Fahrzeuge nötig sind,
            um den Fahrplan zu fahren. Zwei Fahrten lassen sich verketten, wenn ein Fahrzeug
            nach der ersten rechtzeitig zum Start der zweiten gelangt — dazwischen liegen
            Wendezeit und gegebenenfalls eine Leerfahrt.
          </p>
          <p style={{ color: 'var(--grau-700)', lineHeight: 1.7 }}>
            Die Fahrtdauer steht nicht in der Lieferung; sie wird aus den Streckenfahrzeiten
            entlang des Linienverlaufs berechnet. Das Ergebnis ist die kleinstmögliche Anzahl
            von Fahrzeugen — nicht geschätzt, sondern über ein maximales Matching bestimmt.
          </p>
          <p style={{ color: 'var(--grau-500)', fontSize: '0.88rem' }}>
            Die Lieferung wird nur ausgewertet, nicht gespeichert.
          </p>
        </div>
      )}
    </>
  )
}
